<template>
	<div>
		<div>
			<CInput
				v-model.trim="cutomerGroup"
				placeholder="Search by cutomer group"
				class="flex-fill"
				autocomplete="off"
				data-test-id="input-cutomer-group"
			>
				<template #prepend-content>
					<CIcon class="cil-magnifying-glass" name="cil-magnifying-glass" />
				</template>
			</CInput>
		</div>
		<slot
			v-if="!filteredList.length"
			name="no-item"
		>
		</slot>
		<div v-show="filteredList.length" class="list">
			<div class="select-all mb-1 align-items-center">
				<CInputCheckbox
					:checked.sync="isSelectedAll"
					custom
					label="Select all"
					class="select-all-input"
					add-label-classes="font-weight-bold"
					data-test-id="select-all-input"
					@update:checked="handleCheckedSelectAll"
				/>
			</div>
			<DynamicScroller
				:items="filteredList"
				:min-item-size="35"
				key-field="id"
				class="scroller list-select"
			>
				<template v-slot="{ item, index, active }">
					<DynamicScrollerItem
						:item="item"
						:active="active"
						:data-index="index"
						class="list-item"
					>
						<div class="d-flex flex-fill">
							<CInputCheckbox
								:id="`${uid}_${item.id}`"
								:checked="checkItemIsSelected(item.id)"
								custom
								class="list-checkbox overflow-hidden"
								@update:checked="handleCheckedCutomerGroup($event, item)"
							>
								<template #label>
									<label :for="`${uid}_${item.id}`" class="custom-control-label list-label w-100 typo-body-2 font-weight-normal text-nowrap text-truncate color-black-45 mb-0 ml-2">
										<span class="list-cutomer-group color-black">{{ item.name }}</span>
									</label>
								</template>
							</CInputCheckbox>
						</div>
					</DynamicScrollerItem>
				</template>
			</DynamicScroller>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import uniqueId from 'lodash/uniqueId';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
	name: 'FormGetCutomerGroup',
	components: {
		DynamicScroller,
		DynamicScrollerItem,
	},
	props: {
		selectedList: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			uid: null,
			isSelectedAll: false,
			cutomerGroup: '',
			cutomerGroupList: [],
		};
	},
	computed: {
		...mapGetters({
			getCutomerGroupList: 'customerGroups/customerGroupList',
		}),
		filteredList() {
			if (!this.cutomerGroup) {
				return this.cutomerGroupList;
			}

			return this.cutomerGroupList.filter((group) => group.name.toLowerCase().startsWith(this.cutomerGroup.toLowerCase()));
		},
	},
	watch: {
		filteredList() {
			this.checkSelectionState();
		},

		selectedList() {
			this.checkSelectionState();
		},
	},
	async created() {
		await this.getCutomerGroups({ per_page: 'all' });
		this.cutomerGroupList = this.getCutomerGroupList;
	},
	mounted() {
		this.uid = uniqueId('customer_group_checkbox_');
	},
	methods: {
		...mapActions({
			getCutomerGroups: 'customerGroups/getCustomerGroupList',
		}),
		resetState() {
			this.cutomerGroup = '';
		},
		checkItemIsSelected(val) {
			return this.selectedList.some((item) => item.id === val);
		},
		checkSelectionState() {
			const isEverySelected = this.filteredList.length && this.filteredList.every((group) => this.selectedList.map((item) => item.id).includes(group.id));
			const isSomeSelected = this.filteredList.length && this.filteredList.some((group) => this.selectedList.map((item) => item.id).includes(group.id));

			if (isEverySelected) {
				this.setSelectedAllState('checked');
				return;
			}

			if (isSomeSelected) {
				this.setSelectedAllState('indeterminated');
				return;
			}

			this.setSelectedAllState('unchecked');
		},
		setSelectedAllState(state) {
			let isChecked = false;
			let isIndeterminate = false;
			switch (state) {
				case 'checked': {
					isChecked = true;
					isIndeterminate = false;
					break;
				}
				case 'unchecked': {
					isChecked = false;
					isIndeterminate = false;
					break;
				}
				case 'indeterminated': {
					isChecked = true;
					isIndeterminate = true;
					break;
				}
				default: break;
			}

			const selectAllEle = this.$el.querySelector('.select-all-input input');
			if (selectAllEle) {
				selectAllEle.checked = isChecked;
				selectAllEle.indeterminate = isIndeterminate;
			}
		},
		handleCheckedSelectAll() {
			if (this.isSelectedAll) {
				const selectedItems = this.filteredList;
				this.addToSelectedList(selectedItems);
			} else {
				const selectedItems = this.filteredList.map((item) => item.id);
				this.removeFromSelectedList(selectedItems);
			}
		},
		handleCheckedCutomerGroup(checked, group) {
			const isSelected = this.selectedList.some((item) => item.id === group.id);
			if (!isSelected) {
				this.addToSelectedList(group);
			} else {
				this.removeFromSelectedList(group.id);
			}
		},

		/**
		 * @param {String|Array} items Group or List of customer group
		 */
		addToSelectedList(items = []) {
			let newItems = items;
			if (!Array.isArray(items)) {
				newItems = [items];
			}
			const uniqueItems = [...new Set([...this.selectedList, ...newItems])].sort();
			this.$emit('onChange', uniqueItems);
		},

		/**
		 * @param {String|Array} items Group or List of customer group
		 */
		removeFromSelectedList(items = []) {
			let itemsToRemove = items;
			if (!Array.isArray(items)) {
				itemsToRemove = [items];
			}

			this.$emit('onChange', this.selectedList.filter((selectedItem) => !itemsToRemove.includes(selectedItem.id)));
		},
	},
};
</script>

<style lang="scss" scoped>
	.scroller {
		height: 100%;
	}

	.btn-add {
		min-width: rem(84);
	}

	.list {
		margin: 0 -#{rem(16)} -#{rem(16)};
	}

	.select-all {
		background-color: $color-gray-100;
		padding: rem(8) rem(16);
	}

	.list-select {
		min-height: rem(360);
		max-height: rem(360);
		list-style: none;
		padding: 0;
		margin: 0;

		.list-item {
			padding: rem(6) rem(16);

			label {
				margin-top: rem(3);
				cursor: pointer;
			}
		}
	}
</style>
